/**
 * Weekly Learning Loop — the operator's Claude ritual as one governed,
 * scheduled, verifiable mission.
 *
 * Closes the auditor's "loop doesn't close" and "nothing sealed for
 * content" findings by COMPOSING the shipped primitives end to end:
 *
 *   logged   — the post-performance ledger is ingested (postPerformance)
 *   learned  — per-author signals derived with sample-size honesty
 *              (whatWorks.deriveSignals)
 *   proposed — the voice update is a PENDING, human-approved proposal
 *              (authorProfile.proposeProfileUpdate) — never silent drift
 *   generated— drafts run through the REAL worker seam with the shipped
 *              deterministic fallback (promptShaper.generateWithWorker),
 *              so the gate always passes offline
 *   scored   — every draft graded against playbook + personal history
 *              (scoreCalibration.scoreAgainst)
 *   + a sealed-shape OutcomeContract staking the verifiable claim:
 *     "reply rate up 10% over 4 weeks" (outcomeContract.buildOutcomeContract)
 *
 * 'approved' and 'scheduled' are deliberately NOT emitted by the loop run:
 * approval belongs to a human (or to {@link canAutoQueue} once earned) and
 * scheduling belongs to the mission queue — the loop never grants itself
 * those steps.
 *
 * PURE by contract except the single injected `worker.run` call: all state
 * (posts, profile, ideas, the clock) is passed in; nothing here reads a
 * store or performs a write.
 */

import {
  buildOutcomeContract,
  validateOutcomeContract,
  type OutcomeContract,
} from '../agent/outcomeContract';
import type { OutcomeVerdict } from '../agent/outcomeVerifier';
import { scoreMissionItem, type MissionItem } from '../agent/missionQueue';
import { coldStartWeight, deriveSignals } from './whatWorks';
import { loadPlaybook } from './playbookLoader';
import {
  deterministicFallbackDraft,
  generateWithWorker,
  shapePrompt,
} from './promptShaper';
import { scoreAgainst } from './scoreCalibration';
import type { LoggedPost } from './postPerformance';
import {
  proposeProfileUpdate,
  type AuthorProfile,
  type ProfileUpdateProposal,
} from './authorProfile';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Every step the weekly ritual can reach, in canonical order. */
export type LoopStep =
  | 'logged'
  | 'learned'
  | 'proposed'
  | 'approved'
  | 'generated'
  | 'scored'
  | 'scheduled';

/** The full receipt of one weekly loop run. */
export type LoopResult = {
  /** Steps the run actually completed, in order. */
  steps: LoopStep[];
  /** The pending {@link ProfileUpdateProposal} — human-approved, never applied here. */
  proposal: unknown;
  /** One scored draft per input idea. */
  drafts: { text: string; score: number }[];
  /** The sealed-shape outcome contract: reply rate up 10% over 4 weeks. */
  contract: unknown;
  /** Honest generation provenance: real worker or deterministic fallback. */
  source: 'worker' | 'fallback';
};

/** Input to one weekly loop run. All state passed in; worker optional. */
export type WeeklyLoopInput = {
  profile: AuthorProfile;
  loggedPosts: LoggedPost[];
  ideas: string[];
  /** The real worker seam (adapt argoWorker.runAgent); absent → fallback. */
  worker?: { run: (p: string) => Promise<string> };
  /** Contract clock (epoch ms). Defaults to Date.now() at the call site. */
  now?: number;
  /** Format the drafts are generated as. */
  format?: string;
};

// ---------------------------------------------------------------------------
// The outcome claim: reply rate up 10% over 4 weeks
// ---------------------------------------------------------------------------

/** The claim's measurement window: 4 weeks. */
export const REPLY_RATE_WINDOW_MS = 28 * 24 * 60 * 60 * 1000;

/** The claimed relative uplift: +10% over the baseline reply rate. */
export const REPLY_RATE_UPLIFT = 0.1;

const round4 = (x: number) => Math.round(x * 10000) / 10000;

/** Reply rate of one post: replies / views. Honest 0 when unseen. */
export function replyRate(p: LoggedPost): number {
  if (!Number.isFinite(p.metrics.views) || p.metrics.views <= 0) return 0;
  return p.metrics.replies / p.metrics.views;
}

/** Mean reply rate over a set of posts. Honest 0 for an empty set. */
export function meanReplyRate(posts: LoggedPost[]): number {
  if (posts.length === 0) return 0;
  return round4(posts.reduce((s, p) => s + replyRate(p), 0) / posts.length);
}

// ---------------------------------------------------------------------------
// runWeeklyLoop
// ---------------------------------------------------------------------------

/**
 * Run the whole ritual once: ingest → learn → propose → generate → score,
 * then emit the outcome contract the run stakes its value on. The ONLY
 * side effect is the injected `worker.run` call; with no worker the shipped
 * deterministic fallback produces the drafts, so the loop never dies and
 * never fakes a worker. Steps come back in the exact order they completed.
 */
export async function runWeeklyLoop(input: WeeklyLoopInput): Promise<LoopResult> {
  const now = input.now ?? Date.now();
  const format = input.format ?? 'linkedinPost';
  const steps: LoopStep[] = [];

  // 1. logged — the performance ledger is the ingest; it is passed in as
  //    data (never fabricated), so reaching here means metrics are ingested.
  steps.push('logged');

  // 2. learned — derive the candidate signal set with sample-size honesty.
  const nextSignals = deriveSignals(input.loggedPosts);
  steps.push('learned');

  // 3. proposed — the voice update is a pending proposal; NOTHING applied.
  const proposal: ProfileUpdateProposal = proposeProfileUpdate(input.profile, nextSignals);
  steps.push('proposed');

  // 4. generated — shaped prompts through the real worker, deterministic
  //    fallback when it is absent or fails. Sequential so draft order (and
  //    the honest source flag) is stable.
  const playbook = loadPlaybook();
  const generated: { text: string; source: 'worker' | 'fallback' }[] = [];
  for (const idea of input.ideas) {
    const shaped = shapePrompt({
      idea,
      format,
      signalSet: nextSignals,
      playbook,
      totalPosts: input.loggedPosts.length,
    });
    generated.push(
      await generateWithWorker(shaped, input.worker ?? null, deterministicFallbackDraft),
    );
  }
  steps.push('generated');

  // 5. scored — grade every draft against playbook + personal history.
  const drafts = generated.map((g) => ({
    text: g.text,
    score: scoreAgainst(g.text, nextSignals, playbook).overall,
  }));
  steps.push('scored');

  // The verifiable before-claim: reply rate up 10% over 4 weeks. Baseline
  // comes from the real ledger — a zero baseline stakes a zero target and
  // verifyOutcome honestly returns INCONCLUSIVE for it (nothing to beat).
  const baseline = meanReplyRate(input.loggedPosts);
  const contract = buildOutcomeContract(
    { id: proposal.id, action: { kind: 'weekly_content_batch' } },
    {
      kind: 'reply_received',
      now,
      windowMs: REPLY_RATE_WINDOW_MS,
      target: round4(baseline * (1 + REPLY_RATE_UPLIFT)),
      dollarsAtStake: 0,
    },
  );

  return {
    steps,
    proposal,
    drafts,
    contract,
    source: generated.some((g) => g.source === 'worker') ? 'worker' : 'fallback',
  };
}

// ---------------------------------------------------------------------------
// buildWeeklyMission — the scheduler-ready mission entry
// ---------------------------------------------------------------------------

/** How often the mission is due: weekly, like the ritual. */
export const WEEKLY_MISSION_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

/** A missionQueue-shaped entry plus the scheduler's cadence hooks. */
export type WeeklyMission = MissionItem & {
  cadence: 'weekly';
  /** Minimum gap between auto-runs — the agentScheduler due-check input. */
  intervalMs: number;
};

/**
 * Compile the loop into ONE missionQueue-shaped entry the scheduler can
 * rank and fire. Confidence is the transparent cold-start weight (thin
 * ledger → honestly low confidence), dollars are 0 (content missions
 * influence, they never bank), and the deterministic id/proposalRef make a
 * re-run propose the same mission. `scoreMissionItem` accepts the entry
 * unchanged.
 */
export function buildWeeklyMission(input: {
  profile: AuthorProfile;
  loggedPosts: LoggedPost[];
  ideas: string[];
}): WeeklyMission {
  const { profile, loggedPosts, ideas } = input;
  const mission: WeeklyMission = {
    id: `weekly-loop-${profile.authorId}`,
    sourceAgent: 'learningLoop',
    title: `Weekly learning loop for ${profile.authorId}: learn from ${loggedPosts.length} logged posts, draft ${ideas.length} ideas`,
    dollarsAtStake: 0,
    urgency: 0.6,
    confidence: coldStartWeight(loggedPosts.length),
    // Tier 1: the loop PROPOSES learning + drafts; a human approves.
    autonomyTier: 1,
    proposalRef: `profile-update-${profile.authorId}-v${profile.version + 1}`,
    evidence: [
      `${loggedPosts.length} logged post${loggedPosts.length === 1 ? '' : 's'} in the performance ledger`,
      `${ideas.length} idea${ideas.length === 1 ? '' : 's'} queued for drafting`,
      `contract: reply rate up ${Math.round(REPLY_RATE_UPLIFT * 100)}% over 4 weeks`,
    ],
    cadence: 'weekly',
    intervalMs: WEEKLY_MISSION_INTERVAL_MS,
  };
  // Sanity by construction: the entry must be scoreable by the queue.
  void scoreMissionItem(mission);
  return mission;
}

// ---------------------------------------------------------------------------
// canAutoQueue — the earned-autonomy ladder, blast-radius bounded
// ---------------------------------------------------------------------------

/** Minimum draft score an approval must carry to count toward the streak. */
export const AUTO_QUEUE_SCORE_FLOOR = 80;

/**
 * Blast-radius bound: no policy may earn auto-queue on fewer than this
 * many consecutive clean approvals, no matter what `minStreak` asks for —
 * the same "raising autonomy needs evidence" discipline as
 * policyBlastRadius.isSaveBlocked.
 */
export const MIN_AUTO_QUEUE_STREAK = 3;

/**
 * The earned-autonomy ladder for the content loop: auto-queueing drafts is
 * allowed only after the last `minStreak` approvals (bounded below by
 * {@link MIN_AUTO_QUEUE_STREAK}) were ALL high-scoring
 * (≥ {@link AUTO_QUEUE_SCORE_FLOOR}) AND approved unedited. One edit or one
 * low score inside the window resets the earn — a human keeps the pen.
 * Pure and deterministic.
 */
export function canAutoQueue(
  approvalHistory: { scored: number; approvedUnedited: boolean }[],
  policy: { minStreak: number },
): boolean {
  const required = Math.max(
    MIN_AUTO_QUEUE_STREAK,
    Number.isFinite(policy.minStreak) ? Math.ceil(policy.minStreak) : MIN_AUTO_QUEUE_STREAK,
  );
  if (approvalHistory.length < required) return false;
  const window = approvalHistory.slice(-required);
  return window.every(
    (a) => a.approvedUnedited && a.scored >= AUTO_QUEUE_SCORE_FLOOR,
  );
}

// ---------------------------------------------------------------------------
// verifyOutcome — predicted vs actual reply rate (outcomeVerifier pattern)
// ---------------------------------------------------------------------------

/** The loop's measured after-verdict — same three honest verdicts as
 *  lib/agent/outcomeVerifier, plus the predicted/actual pair. */
export type ReplyRateOutcome = {
  proposalId: string;
  verdict: OutcomeVerdict;
  /** The contract's staked reply-rate target. */
  predicted: number;
  /** The measured mean reply rate of posts logged inside the window. */
  actual: number;
  evidence: string[];
};

/**
 * Replay the logged ledger against the loop's contract, following the
 * outcomeVerifier pattern: unknown is NEVER assumed positive.
 *
 *   • malformed contract        → INCONCLUSIVE, citing every validation error
 *   • zero staked target        → INCONCLUSIVE (no baseline existed to beat)
 *   • no posts inside window    → INCONCLUSIVE (reality unmeasured)
 *   • measured ≥ target         → VERIFIED, else MISSED
 *
 * Pure: posts are passed in; no clock, no store, no writes.
 */
export function verifyOutcome(contract: unknown, posts: LoggedPost[]): ReplyRateOutcome {
  const check = validateOutcomeContract(contract);
  if (!check.ok) {
    return {
      proposalId: '',
      verdict: 'INCONCLUSIVE',
      predicted: 0,
      actual: 0,
      evidence: check.errors.map((e) => `invalid contract: ${e}`),
    };
  }

  const c = contract as OutcomeContract;
  const { start, end } = c.predicate.window;
  const target = c.predicate.target ?? 0;

  if (target <= 0) {
    return {
      proposalId: c.proposalId,
      verdict: 'INCONCLUSIVE',
      predicted: target,
      actual: 0,
      evidence: ['no baseline reply rate was staked — nothing measurable to beat'],
    };
  }

  const inWindow = posts.filter((p) => p.loggedAt >= start && p.loggedAt <= end);
  if (inWindow.length === 0) {
    return {
      proposalId: c.proposalId,
      verdict: 'INCONCLUSIVE',
      predicted: target,
      actual: 0,
      evidence: [`no posts logged inside window ${start}–${end}`],
    };
  }

  const actual = meanReplyRate(inWindow);
  const verdict: OutcomeVerdict = actual >= target ? 'VERIFIED' : 'MISSED';
  return {
    proposalId: c.proposalId,
    verdict,
    predicted: target,
    actual,
    evidence: [
      `measured reply rate ${actual} vs staked ${target} across ${inWindow.length} post${
        inWindow.length === 1 ? '' : 's'
      } in window`,
      ...inWindow.map((p) => `post:${p.id} reply rate ${round4(replyRate(p))}`),
    ],
  };
}
